import HeaderMenu from 'components/Header/HeaderMenu';
import { SITE } from 'config/config';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getWorldCupSchedule } from 'service/base';
import { twMerge } from 'tailwind-merge';
import KnockoutChart from './KnockoutChart';

import './worldCupSchedule.css';

const CM88_LINK = 'https://cm88.com/home/match-news';

function normalize(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd');
}

function Flag({ teamName, flagCodes }) {
  const code = flagCodes?.[teamName];
  if (!code) {
    return (
      <span className="ball" aria-hidden="true">
        ⚽
      </span>
    );
  }

  return (
    <img
      className="flag"
      src={`https://flagcdn.com/w40/${code}.png`}
      alt={teamName}
      loading="lazy"
      draggable={false}
    />
  );
}

function sortDatesForSelect(dates) {
  return [...dates].sort((a, b) => {
    const pa = String(a || '')
      .trim()
      .split(/[/\-]/)
      .map(Number);
    const pb = String(b || '')
      .trim()
      .split(/[/\-]/)
      .map(Number);
    const da = pa[0] || 0;
    const ma = pa[1] || 0;
    const ya = pa[2] || 2026;
    const db = pb[0] || 0;
    const mb = pb[1] || 0;
    const yb = pb[2] || 2026;
    if (ya !== yb) return ya - yb;
    if (ma !== mb) return ma - mb;
    return da - db;
  });
}

/** Lọc trận theo ô tìm kiếm + ngày + vòng + bảng (dùng chung cho filtered + thu gọn mobile). */
function filterScheduleMatches(matches, { search, scheduleDate, round, group }) {
  const kw = normalize(String(search || '').trim());
  const isFinalStageBundle = round === 'Tranh Hạng Ba' || round === 'Chung Kết';

  return matches.filter((item) => {
    const haystack = normalize(
      `${item.section} ${item.group} ${item.date} ${item.time} ${item.match} ${item.home} ${item.away}`
    );
    const dateOk = !scheduleDate || String(item.date || '').trim() === String(scheduleDate).trim();
    const roundOk = !round
      ? true
      : isFinalStageBundle
      ? item.section === 'Tranh Hạng Ba' || item.section === 'Chung Kết'
      : item.section === round;
    return (
      dateOk &&
      roundOk &&
      (!group || item.group === group) &&
      (!kw || haystack.includes(kw))
    );
  });
}

const WorldCupShedule = () => {
  const [activeTab, setActiveTab] = useState('knockout');
  const [search, setSearch] = useState('');
  const [scheduleDate, setScheduleDate] = useState('');
  const [round, setRound] = useState('');
  const [group, setGroup] = useState('');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [matches, setMatches] = useState([]);
  const [flagCodes, setFlagCodes] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState('');

  const loadSchedule = useCallback(async () => {
    setLoading(true);
    setLoadErr('');
    try {
      const res = await getWorldCupSchedule();
      if (res?.code === 0 && res?.data?.matches?.length) {
        setMatches(res.data.matches);
        setFlagCodes(res.data.flagCodes || {});
        setRound((prev) => prev || 'Tranh Hạng Ba');
      } else {
        setMatches([]);
        setFlagCodes({});
        setLoadErr(res?.message || 'Không tải được lịch thi đấu.');
      }
    } catch (e) {
      setLoadErr(e?.message || 'Lỗi mạng khi tải lịch.');
      setMatches([]);
      setFlagCodes({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSchedule();
  }, [loadSchedule]);

  useEffect(() => {
    const prev = document.title;
    document.title =
      activeTab === 'knockout'
        ? 'Sơ đồ vòng Knockout World Cup 2026 - CM88'
        : 'Lịch thi đấu World Cup 2026 - CM88';
    return () => {
      document.title = prev;
    };
  }, [activeTab]);

  const rounds = useMemo(() => [...new Set(matches.map((item) => item.section))], [matches]);
  const groups = useMemo(() => [...new Set(matches.map((item) => item.group))], [matches]);
  const scheduleDates = useMemo(
    () => sortDatesForSelect([...new Set(matches.map((item) => item.date).filter(Boolean))]),
    [matches]
  );

  const filtered = useMemo(
    () => filterScheduleMatches(matches, { search, scheduleDate, round, group }),
    [search, scheduleDate, round, group, matches]
  );

  const collapseMobileIfHasResults = useCallback(
    (overrides = {}) => {
      if (typeof window === 'undefined') return;
      const next = filterScheduleMatches(matches, {
        search: overrides.search !== undefined ? overrides.search : search,
        scheduleDate: overrides.scheduleDate !== undefined ? overrides.scheduleDate : scheduleDate,
        round: overrides.round !== undefined ? overrides.round : round,
        group: overrides.group !== undefined ? overrides.group : group
      });
      if (next.length > 0) {
        setMobileFiltersOpen(false);
      }
    },
    [matches, search, scheduleDate, round, group]
  );

  const groupedBySection = useMemo(() => {
    const blocks = [];

    filtered.forEach((item) => {
      const prev = blocks[blocks.length - 1];
      if (!prev || prev.section !== item.section) {
        blocks.push({ section: item.section, items: [item] });
      } else {
        prev.items.push(item);
      }
    });

    return blocks;
  }, [filtered]);

  return (
    <div className={twMerge(SITE, 'w-full min-h-full')}>
      <div className="relative z-10 pb-5">
        <HeaderMenu />
        <div className="container relative z-10 px-2">
          <div className="w-[90%] max-w-[500px] mx-auto mt-4 mb-4">
            <div className="wc-switch-tabs">
              <button
                type="button"
                className={twMerge(
                  'wc-switch-tab',
                  activeTab === 'schedule' && 'wc-switch-tab--active'
                )}
                onClick={() => setActiveTab('schedule')}
              >
                Lịch thi đấu
              </button>
              <button
                type="button"
                className={twMerge(
                  'wc-switch-tab',
                  activeTab === 'knockout' && 'wc-switch-tab--active'
                )}
                onClick={() => setActiveTab('knockout')}
              >
                Sơ đồ vòng Knockout
              </button>
            </div>
          </div>

          {activeTab === 'knockout' ? (
            <KnockoutChart embedded />
          ) : (
            <div className="wc-schedule-page rounded-3xl border-solid border-[#36A638] border-[3px]">
              <div className="page">
                <header className="hero">
                  <h1>Lịch thi đấu World Cup 2026</h1>
                  <div className="sub">
                    Cập nhật kết quả các trận đấu Vòng Chung kết World Cup 2026
                  </div>
                  <div className="stats">
                    <div className="stat">
                      <b>48</b>
                      <span>Đội tuyển</span>
                    </div>
                    <div className="stat">
                      <b>104</b>
                      <span>Trận đấu</span>
                    </div>
                    <div className="stat">
                      <b>16</b>
                      <span>Thành phố</span>
                    </div>
                    <div className="stat">
                      <b>3</b>
                      <span>Quốc gia</span>
                    </div>
                  </div>
                </header>

                {loadErr ? (
                  <div className="toolbar flex flex-wrap items-center gap-3">
                    <div className="empty text-left">{loadErr}</div>
                    <button
                      type="button"
                      className="field w-auto shrink-0"
                      onClick={() => loadSchedule()}
                    >
                      Thử lại
                    </button>
                  </div>
                ) : null}

                <div className="wc-quick-filter-bar">
                  <button
                    type="button"
                    className={twMerge(
                      'wc-quick-filter-btn',
                      mobileFiltersOpen && 'wc-quick-filter-btn--open'
                    )}
                    aria-expanded={mobileFiltersOpen}
                    aria-controls="wc-schedule-filters"
                    disabled={loading || !!loadErr}
                    onClick={() => setMobileFiltersOpen((prev) => !prev)}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      className="shrink-0"
                      aria-hidden
                    >
                      <circle cx="11" cy="11" r="7" />
                      <path d="M21 21l-4.35-4.35" />
                    </svg>
                    <span>Bộ lọc nhanh</span>
                  </button>
                </div>

                <div
                  id="wc-schedule-filters"
                  className={twMerge(
                    'toolbar opacity-90',
                    mobileFiltersOpen && 'wc-schedule-filters--open'
                  )}
                  aria-busy={loading}
                >
                  <input
                    aria-label="Tìm trận đấu"
                    className="field"
                    type="search"
                    placeholder="Tìm đội tuyển, vòng đấu, mã trận..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        collapseMobileIfHasResults();
                      }
                    }}
                    disabled={loading || !!loadErr}
                  />
                  <select
                    aria-label="Lọc theo ngày"
                    className="field"
                    value={scheduleDate}
                    onChange={(e) => {
                      const v = e.target.value;
                      setScheduleDate(v);
                      collapseMobileIfHasResults({ scheduleDate: v });
                    }}
                    disabled={loading || !!loadErr}
                  >
                    <option value="">Tất cả các ngày</option>
                    {scheduleDates.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                  <select
                    aria-label="Lọc vòng đấu"
                    className="field"
                    value={round}
                    onChange={(e) => {
                      const v = e.target.value;
                      setRound(v);
                      collapseMobileIfHasResults({ round: v });
                    }}
                    disabled={loading || !!loadErr}
                  >
                    <option value="">Tất cả vòng đấu</option>
                    {rounds.map((r) => (
                      <option key={r} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>
                  <select
                    aria-label="Lọc bảng"
                    className="field"
                    value={group}
                    onChange={(e) => {
                      const v = e.target.value;
                      setGroup(v);
                      collapseMobileIfHasResults({ group: v });
                    }}
                    disabled={loading || !!loadErr}
                  >
                    <option value="">Tất cả bảng</option>
                    {groups.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="table-head">
                  <div>Bảng đấu</div>
                  <div>Giờ thi đấu</div>
                  <div>Mã trận</div>
                  <div>Trận đấu</div>
                  <div>Trực tiếp</div>
                </div>

                <main id="scheduleRoot">
                  {loading ? (
                    <div className="empty">Đang tải lịch thi đấu…</div>
                  ) : loadErr ? null : filtered.length === 0 ? (
                    <div className="empty">Không tìm thấy trận đấu phù hợp.</div>
                  ) : (
                    groupedBySection.map(({ section, items }) => (
                      <div key={section}>
                        <div className="section-title">{section}</div>
                        <div className="match-list">
                          {items.map((item) => (
                            <article key={item.id} className="match-row">
                              <div className="group">{item.group}</div>
                              <div className="datetime">
                                <span className="dt-date">{item.date}</span>
                                <span className="dt-time">{item.time}</span>
                              </div>
                              <div className="match-id">{item.match}</div>
                              <div className="teams">
                                <div className="team home">
                                  <span>{item.home}</span>
                                  <Flag teamName={item.home} flagCodes={flagCodes} />
                                </div>
                                <div className="vs">
                                  {item.result ? (
                                    <span className="result">{item.result}</span>
                                  ) : (
                                    '-'
                                  )}
                                </div>
                                <div className="team away">
                                  <Flag teamName={item.away} flagCodes={flagCodes} />
                                  <span>{item.away}</span>
                                </div>
                              </div>
                              <div className="live-cell">
                                <a
                                  className="live-btn"
                                  href={CM88_LINK}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <span className="live-dot" aria-hidden="true" />
                                  LIVE
                                </a>
                              </div>
                            </article>
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </main>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorldCupShedule;
